/**
 * PromptLibrary — slide-out drawer in AI Studio that surfaces 64 curated
 * starter prompts grouped by hub. Click a prompt → it loads into the chat
 * input ready to edit/send.
 *
 * Source of prompts: /app/frontend/src/lib/hubPrompts.js
 *  - HUB_DEFINITIONS: 8 hubs (legal, hr, marketing, knowledge, crm, finance,
 *    operations, productivity) with color + tagline.
 *  - HUB_PROMPTS:     8 prompts per hub (id, title, hint, prompt body).
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import {
  Sparkles, Search, ArrowRight, X, Clock, Pin, Plus, Trash2, FolderPlus,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { CBLogo } from '../CBLogo';
import { HUB_DEFINITIONS, HUB_PROMPTS } from '../../lib/hubPrompts';

// Uncategorized "virtual hub" — used to group user-saved prompts.
const UNCATEGORIZED_HUB = {
  key: 'uncategorized',
  name: 'My Prompts',
  tagline: 'Your saved prompts',
  icon: 'FolderPlus',
  color: 'amber',
  accent: 'from-amber-500 to-orange-500',
  description: 'Prompts you saved to the library.',
};

const colorMap = {
  amber: 'from-amber-500 to-orange-500',
  rose: 'from-rose-500 to-pink-500',
  violet: 'from-violet-500 to-fuchsia-500',
  sky: 'from-sky-500 to-cyan-500',
  emerald: 'from-emerald-500 to-teal-500',
  green: 'from-green-500 to-emerald-500',
  blue: 'from-blue-500 to-indigo-500',
  yellow: 'from-yellow-500 to-amber-500',
};

// localStorage key for recently-used prompt usage map
//   { "legal:nda": { count: 5, last_used: 1708812345678 }, ... }
const RECENT_LS_KEY = 'ai_studio_recent_prompts_v1';
const RECENT_LIMIT = 5;          // how many to surface at the top
const RECENT_MIN_DISTINCT = 1;   // show the section after the first use

// localStorage key for user-created prompts (saved into the library)
//   [{ id, title, hint, prompt, hub: 'uncategorized', created_at }]
const CUSTOM_LS_KEY = 'ai_studio_custom_prompts_v1';

function loadCustomPrompts() {
  try {
    const raw = localStorage.getItem(CUSTOM_LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveCustomPrompts(list) {
  try { localStorage.setItem(CUSTOM_LS_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

function loadRecentMap() {
  try {
    const raw = localStorage.getItem(RECENT_LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveRecentMap(map) {
  try { localStorage.setItem(RECENT_LS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

function bumpUsage(hubKey, promptId) {
  const map = loadRecentMap();
  const k = `${hubKey}:${promptId}`;
  const prev = map[k] || { count: 0, last_used: 0 };
  map[k] = { count: prev.count + 1, last_used: Date.now() };
  saveRecentMap(map);
  return map;
}

// Return top-N prompts sorted by combined frequency + recency score.
// Score = count + recencyBoost (1.0 if used in last 24h, 0.5 within 7d, else 0).
function pickRecent(map, limit) {
  const now = Date.now();
  const DAY = 86400000;
  const entries = Object.entries(map).map(([k, v]) => {
    const [hubKey, promptId] = k.split(':');
    const age = now - (v.last_used || 0);
    const recency = age < DAY ? 1.0 : age < 7 * DAY ? 0.5 : 0;
    return { hubKey, promptId, count: v.count, last_used: v.last_used, score: v.count + recency };
  });
  entries.sort((a, b) => b.score - a.score || b.last_used - a.last_used);
  return entries.slice(0, limit);
}

export function PromptLibrary({ onSelectPrompt, triggerClassName, initialHubFilter, onConsumed }) {
  const [open, setOpen] = useState(false);
  const [activeHub, setActiveHub] = useState('all');
  const [search, setSearch] = useState('');
  const [recentMap, setRecentMap] = useState({});
  const [customPrompts, setCustomPrompts] = useState([]);
  const [showCreator, setShowCreator] = useState(false);

  // Auto-open + filter when an initialHubFilter is passed (e.g. linked from
  // AI Command Center via ?promptHub=marketing). Notify parent so it can
  // clean up the URL param after consumption.
  useEffect(() => {
    if (initialHubFilter) {
      setActiveHub(initialHubFilter);
      setOpen(true);
      onConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHubFilter]);

  // Sync recent-usage + custom prompts from localStorage when drawer opens
  useEffect(() => {
    if (open) {
      setRecentMap(loadRecentMap());
      setCustomPrompts(loadCustomPrompts());
    }
  }, [open]);

  // Merged hub list — system hubs + "My Prompts" if user has saved any.
  const hubs = useMemo(() => {
    const list = Object.values(HUB_DEFINITIONS);
    if (customPrompts.length > 0) list.push(UNCATEGORIZED_HUB);
    return list;
  }, [customPrompts.length]);

  // hub -> list of prompts (system map + dynamic uncategorized)
  const allHubPrompts = useMemo(() => ({
    ...HUB_PROMPTS,
    uncategorized: customPrompts,
  }), [customPrompts]);

  const totalCount = useMemo(
    () => Object.values(HUB_PROMPTS).reduce((a, p) => a + p.length, 0) + customPrompts.length,
    [customPrompts.length],
  );

  // Resolve "recently used" rows. Falls back to nothing if user hasn't used any.
  const recentRows = useMemo(() => {
    if (search.trim() || activeHub !== 'all') return [];
    const distinctCount = Object.keys(recentMap).length;
    if (distinctCount < RECENT_MIN_DISTINCT) return [];
    return pickRecent(recentMap, RECENT_LIMIT).map(({ hubKey, promptId, count }) => {
      const hub = hubKey === 'uncategorized' ? UNCATEGORIZED_HUB : HUB_DEFINITIONS[hubKey];
      const prompt = (allHubPrompts[hubKey] || []).find((p) => p.id === promptId);
      if (!hub || !prompt) return null;
      return { hub, prompt, count };
    }).filter(Boolean);
  }, [recentMap, search, activeHub, allHubPrompts]);

  // Flatten with hub metadata; filter by hub + search query.
  const filteredPrompts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = [];
    hubs.forEach((hub) => {
      if (activeHub !== 'all' && activeHub !== hub.key) return;
      (allHubPrompts[hub.key] || []).forEach((p) => {
        if (q) {
          const hay = `${p.title} ${p.hint} ${p.prompt} ${hub.name}`.toLowerCase();
          if (!hay.includes(q)) return;
        }
        rows.push({ hub, prompt: p });
      });
    });
    return rows;
  }, [hubs, activeHub, search, allHubPrompts]);

  const handlePick = useCallback((hubKey, promptId, promptBody) => {
    bumpUsage(hubKey, promptId);
    onSelectPrompt?.(promptBody);
    setOpen(false);
  }, [onSelectPrompt]);

  const handleClearRecent = useCallback((e) => {
    e?.stopPropagation();
    saveRecentMap({});
    setRecentMap({});
  }, []);

  const handleSaveCustomPrompt = useCallback((title, body) => {
    if (!title?.trim() || !body?.trim()) {
      toast.error('Title and prompt body are required');
      return false;
    }
    const newPrompt = {
      id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      title: title.trim().slice(0, 80),
      hint: body.trim().slice(0, 100),
      prompt: body.trim(),
      created_at: Date.now(),
    };
    const next = [newPrompt, ...customPrompts].slice(0, 100);
    saveCustomPrompts(next);
    setCustomPrompts(next);
    toast.success('Prompt saved to My Prompts');
    return true;
  }, [customPrompts]);

  const handleDeleteCustomPrompt = useCallback((id, e) => {
    e?.stopPropagation();
    const next = customPrompts.filter((p) => p.id !== id);
    saveCustomPrompts(next);
    setCustomPrompts(next);
    toast.success('Prompt deleted');
  }, [customPrompts]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  // Slight emerald tint + sparkle ring so users discover the
                  // library instead of treating it like another grey button.
                  'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-zinc-900 border-emerald-200 dark:border-emerald-500/30 text-zinc-700 dark:text-zinc-200 hover:from-emerald-100 hover:to-white dark:hover:from-emerald-900/40 gap-1.5',
                  triggerClassName,
                )}
                data-testid="prompt-library-trigger"
              >
                <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-xs">Prompts</span>
                <Badge variant="secondary" className="ml-0.5 px-1.5 py-0 h-4 text-[9px] font-mono bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-500/30">
                  {Object.values(HUB_PROMPTS).reduce((a, p) => a + p.length, 0)}
                </Badge>
              </Button>
            </SheetTrigger>
          </TooltipTrigger>
          <TooltipContent side="top" align="center" className="max-w-[280px] leading-snug">
            <div className="font-semibold mb-1">📚 Prompt Library</div>
            <p className="text-[11px] opacity-90">
              Browse 64 ready-to-use prompts grouped by hub — Finance, HR, Marketing, Legal, Ops, and more. Click any prompt to drop it into your composer, then tweak and send. You can also save your own.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <SheetContent
        side="right"
        className="w-full sm:max-w-[75vw] lg:max-w-[850px] p-0 flex flex-col bg-white dark:bg-zinc-950 border-l border-zinc-200 dark:border-white/10"
        data-testid="prompt-library-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 dark:border-white/5">
          <div className="flex items-center gap-3">
            <CBLogo size="lg" className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-lg font-bold leading-tight">Prompt Library</SheetTitle>
              <SheetDescription className="text-xs text-zinc-500">
                {totalCount} ready-to-use prompts. Click any to load into the chat.
              </SheetDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCreator(true)}
              className="gap-1.5 border-emerald-500/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
              data-testid="prompt-library-new-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="text-xs">New prompt</span>
            </Button>
          </div>

          {/* Search */}
          <div className="relative mt-3">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Search prompts…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 bg-zinc-50 dark:bg-zinc-900/50 border-zinc-200 dark:border-white/10"
              data-testid="prompt-library-search"
              autoFocus
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Hub filter pills */}
          <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
            <FilterPill
              active={activeHub === 'all'}
              onClick={() => setActiveHub('all')}
              label="All"
              count={totalCount}
              testId="prompt-filter-all"
            />
            {hubs.map((hub) => (
              <FilterPill
                key={hub.key}
                active={activeHub === hub.key}
                onClick={() => setActiveHub(hub.key)}
                label={hub.name.replace(' Hub', '')}
                count={(allHubPrompts[hub.key] || []).length}
                gradient={colorMap[hub.color]}
                testId={`prompt-filter-${hub.key}`}
              />
            ))}
          </div>
        </SheetHeader>

        {/* Inline create-prompt form */}
        {showCreator && (
          <CustomPromptCreator
            onSave={(title, body) => {
              const ok = handleSaveCustomPrompt(title, body);
              if (ok) setShowCreator(false);
            }}
            onClose={() => setShowCreator(false)}
          />
        )}

        {/* Prompt list — 2-column grid on wider drawer */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {/* Recently used pinned-row — shown only when there's no search/filter active */}
            {recentRows.length > 0 && (
              <div className="mb-3" data-testid="prompt-recent-section">
                <div className="flex items-center justify-between mb-1.5 px-0.5">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400">
                      Recently used
                    </span>
                    <Badge variant="secondary" className="px-1.5 py-0 h-4 text-[9px] font-mono">
                      {recentRows.length}
                    </Badge>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearRecent}
                    className="text-[10px] text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
                    data-testid="prompt-recent-clear"
                  >
                    Clear
                  </button>
                </div>
                <div className="space-y-1.5">
                  {recentRows.map(({ hub, prompt, count }) => {
                    const Icon = LucideIcons[hub.icon] || Sparkles;
                    const gradient = colorMap[hub.color] || colorMap.blue;
                    return (
                      <button
                        type="button"
                        key={`recent-${hub.key}-${prompt.id}`}
                        onClick={() => handlePick(hub.key, prompt.id, prompt.prompt)}
                        className="w-full text-left rounded-xl border border-emerald-500/30 dark:border-emerald-500/20 bg-gradient-to-r from-emerald-50/60 via-white to-white dark:from-emerald-950/30 dark:via-zinc-900/40 dark:to-zinc-900/40 hover:border-emerald-500/60 hover:from-emerald-50 dark:hover:from-emerald-950/50 transition-all p-2.5 group relative"
                        data-testid={`prompt-recent-${hub.key}-${prompt.id}`}
                      >
                        <Pin className="absolute top-1.5 right-1.5 w-2.5 h-2.5 text-emerald-500/60 dark:text-emerald-400/60" />
                        <div className="flex items-center gap-2.5">
                          <div className={cn(
                            'w-6 h-6 rounded-md flex items-center justify-center text-white flex-shrink-0 bg-gradient-to-br shadow-sm',
                            gradient,
                          )}>
                            <Icon className="w-3 h-3" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 truncate">{prompt.title}</p>
                              <span className="text-[8px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-600 flex-shrink-0">
                                {hub.name.replace(' Hub', '')}
                              </span>
                              <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-mono flex-shrink-0" title={`Used ${count} time${count === 1 ? '' : 's'}`}>
                                ×{count}
                              </span>
                            </div>
                          </div>
                          <ArrowRight className="w-3 h-3 text-zinc-300 dark:text-zinc-700 group-hover:text-emerald-500 transition-colors flex-shrink-0" />
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="my-3 border-t border-dashed border-zinc-200 dark:border-white/5" />
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-400 dark:text-zinc-600 px-0.5 mb-1">
                  All prompts
                </p>
              </div>
            )}

            {filteredPrompts.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-12 italic">
                {search ? `No prompts match "${search}"` : (
                  <>
                    No prompts here yet.{' '}
                    <button
                      type="button"
                      onClick={() => setShowCreator(true)}
                      className="text-emerald-600 dark:text-emerald-400 font-medium hover:underline"
                    >
                      Create your first one →
                    </button>
                  </>
                )}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredPrompts.map(({ hub, prompt }) => {
                  const Icon = LucideIcons[hub.icon] || Sparkles;
                  const gradient = colorMap[hub.color] || colorMap.blue;
                  const isCustom = hub.key === 'uncategorized';
                  return (
                    <button
                      type="button"
                      key={`${hub.key}-${prompt.id}`}
                      onClick={() => handlePick(hub.key, prompt.id, prompt.prompt)}
                      className="w-full text-left rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/40 hover:border-emerald-500/40 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 transition-all p-3.5 group relative"
                      data-testid={`prompt-card-${hub.key}-${prompt.id}`}
                    >
                      {isCustom && (
                        <button
                          type="button"
                          onClick={(e) => handleDeleteCustomPrompt(prompt.id, e)}
                          className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                          title="Delete prompt"
                          data-testid={`prompt-delete-${prompt.id}`}
                        >
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </button>
                      )}
                      <div className="flex items-start gap-2.5">
                        <div className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0 bg-gradient-to-br',
                          gradient,
                        )}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">{prompt.title}</p>
                            <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-600 flex-shrink-0">
                              {hub.name.replace(' Hub', '')}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-3 leading-relaxed">{prompt.hint}</p>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-zinc-300 dark:text-zinc-700 group-hover:text-emerald-500 transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="border-t border-zinc-200 dark:border-white/5 px-4 py-2.5 bg-zinc-50/60 dark:bg-zinc-900/30">
          <p className="text-[10px] text-zinc-500 dark:text-zinc-600 text-center">
            Tip: prompts often contain <code className="px-1 py-0.5 bg-zinc-200 dark:bg-zinc-800 rounded">[PLACEHOLDERS]</code> — replace them before sending.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FilterPill({ active, onClick, label, count, gradient, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all flex items-center gap-1.5 border',
        active
          ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 border-zinc-900 dark:border-white'
          : 'bg-white dark:bg-zinc-900/40 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-white/10 hover:border-zinc-400 dark:hover:border-white/20',
      )}
      data-testid={testId}
    >
      {gradient && !active && (
        <span className={cn('w-1.5 h-1.5 rounded-full bg-gradient-to-br', gradient)} />
      )}
      {label}
      <span className={cn(
        'text-[9px] font-mono px-1 rounded',
        active ? 'bg-white/20 dark:bg-zinc-900/20' : 'bg-zinc-100 dark:bg-zinc-800',
      )}>
        {count}
      </span>
    </button>
  );
}

// CustomPromptCreator — inline form panel inside the drawer for saving
// a user-defined prompt into the "My Prompts" (uncategorized) section.
function CustomPromptCreator({ onSave, onClose }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  return (
    <div className="border-b border-zinc-200 dark:border-white/5 bg-emerald-50/30 dark:bg-emerald-950/20 p-4" data-testid="prompt-creator">
      <div className="flex items-center gap-2 mb-3">
        <FolderPlus className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Save a new prompt</h3>
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 ml-auto">→ My Prompts</span>
      </div>
      <div className="space-y-2">
        <Input
          placeholder="Prompt title (e.g. Weekly Client Check-in)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-9 bg-white dark:bg-zinc-950 border-zinc-200 dark:border-white/10"
          data-testid="prompt-creator-title"
          maxLength={80}
          autoFocus
        />
        <Textarea
          placeholder="Paste or write your prompt here. Use [PLACEHOLDERS] for anything you'll customize per use."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="resize-none bg-white dark:bg-zinc-950 border-zinc-200 dark:border-white/10 text-sm"
          data-testid="prompt-creator-body"
        />
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <Button variant="ghost" size="sm" onClick={onClose} className="text-xs" data-testid="prompt-creator-cancel">
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => onSave?.(title, body)}
          disabled={!title.trim() || !body.trim()}
          className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs gap-1.5"
          data-testid="prompt-creator-save"
        >
          <Plus className="w-3 h-3" /> Save prompt
        </Button>
      </div>
    </div>
  );
}
